import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * History-aware back. Pops history when the previous entry is in-app, else
 * REPLACES to the route's fallback (parent) path so browser-back after a
 * fallback jump still leaves the site exactly once.
 *
 * Detection: newer routers write `idx` into history.state — prefer it. Our
 * installed @remix-run/router@1.0.2 doesn't (idx arrived in 6.5+), so fall
 * back to the location-key idiom: a fresh (deep-link) entry restores key
 * 'default'. Known gap: a deep link that lands on a <Navigate replace> shim
 * gets a fresh random key, so back behaves like browser-back there — same as
 * the browser chrome, and self-heals once the router is upgraded.
 */
export const useBack = (fallback: string = '/') => {
	const navigate = useNavigate()
	const { key } = useLocation()
	return useCallback(() => {
		const idx = (window.history.state as { idx?: number } | null)?.idx
		const canGoBack = typeof idx === 'number' ? idx > 0 : key !== 'default'
		if (canGoBack) navigate(-1)
		else navigate(fallback, { replace: true })
	}, [navigate, key, fallback])
}
